import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import MethodCard from './components/MethodCard';
import CancelButton from './components/CancelButton';
import apiClient from '../../api/client';
import StructureImage from '../../components/chemistry/StructureImage';
import * as $3Dmol from '3dmol';
import WorkbenchContextBar from '../../components/workbench/WorkbenchContextBar';
import ToolSwitcher from '../../components/workbench/ToolSwitcher';
import { useToolStep, useWorkbenchContext } from '../../hooks/useWorkbenchContext';

type AnalysisType = 'liability' | 'predict' | 'secondary' | 'full' | 'cyp-dock';

const ANALYSIS_TABS: { key: AnalysisType; label: string; desc: string }[] = [
  { key: 'liability', label: 'Metabolic Liability', desc: 'Identify metabolic soft spots and risk level' },
  { key: 'predict', label: 'Primary Metabolite Prediction', desc: 'Predict primary Phase I CYP-mediated metabolites' },
  { key: 'secondary', label: 'Secondary Metabolism', desc: 'Phase II conjugation predictions' },
  { key: 'full', label: 'Full Analysis', desc: 'Combined Phase I prediction + liability scoring' },
  { key: 'cyp-dock', label: 'CYP Docking', desc: 'Dock into CYP active site with AutoDock Vina' },
];

/* ---------- 3Dmol Docking Viewer ---------- */
const DockingPoseViewer: React.FC<{ poseData: string; bestAffinity: number; receptor: string; currentPose?: number; onPoseChange?: (p: number) => void }> = ({ poseData, bestAffinity, receptor, currentPose, onPoseChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<any>(null);
  const [spinning, setSpinning] = useState(false);
  const [internalPose, setInternalPose] = useState(1);

  const activePose = currentPose ?? internalPose;
  const setActivePose = onPoseChange ?? setInternalPose;

  const poseCount = useMemo(() => (poseData.match(/^MODEL\s+/gm) || []).length, [poseData]);

  useEffect(() => {
    if (!containerRef.current || !poseData) return;
    const timer = setTimeout(() => {
      if (!containerRef.current) return;
      if (viewerRef.current) {
        viewerRef.current.removeAllModels();
      } else {
        viewerRef.current = ($3Dmol as any).createViewer(containerRef.current, { backgroundColor: '#1a1d22', antialias: true });
      }
      const models = poseData.split(/(?=^MODEL\s+)/m);
      const selectedModel = models[activePose] || models[1] || poseData;
      viewerRef.current.addModel(selectedModel, 'pdb');
      viewerRef.current.setStyle({}, { stick: { radius: 0.14, colorscheme: 'Jmol' }, sphere: { scale: 0.25, colorscheme: 'Jmol' } });
      viewerRef.current.zoomTo();
      viewerRef.current.render();
    }, 50);
    return () => clearTimeout(timer);
  }, [poseData, activePose]);

  const toggleSpin = () => {
    if (!viewerRef.current) return;
    viewerRef.current.spin(spinning ? false : 'y');
    setSpinning(!spinning);
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#0D9488' }}>
          Docked Pose — {receptor}
        </span>
        <span style={{ fontSize: 12, fontFamily: 'monospace', color: bestAffinity < -7 ? '#16a34a' : bestAffinity < -5 ? '#d97706' : '#dc2626' }}>
          {bestAffinity.toFixed(1)} kcal/mol
        </span>
      </div>
      <div style={{ position: 'relative', maxWidth: 480 }}>
        <div ref={containerRef} style={{ width: '100%', aspectRatio: '1', borderRadius: 8, border: '1px solid var(--card-border)', overflow: 'hidden' }} />
        <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
          {poseCount > 1 && (
            <select value={activePose} onChange={(e) => setActivePose(Number(e.target.value))}
              style={{ fontSize: 11, borderRadius: 4, padding: '2px 4px', backgroundColor: 'rgba(0,0,0,0.5)', color: '#a7abb6', border: 'none', outline: 'none' }}>
              {Array.from({ length: Math.min(poseCount, 9) }, (_, i) => (
                <option key={i + 1} value={i + 1}>Pose {i + 1}</option>
              ))}
            </select>
          )}
          <button onClick={toggleSpin} title={spinning ? 'Stop rotation' : 'Rotate'}
            style={{ padding: 4, borderRadius: 4, backgroundColor: spinning ? 'rgba(13,148,136,0.3)' : 'rgba(0,0,0,0.4)', color: spinning ? '#5eead4' : '#a7abb6', border: 'none', cursor: 'pointer', fontSize: 11 }}>
            &#x21bb;
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------- SMILES SVG Cell ---------- */
const SmilesSvgCell: React.FC<{ smiles: string; svgMap: Record<string, string> }> = ({ smiles, svgMap }) => {
  if (svgMap[smiles]) {
    return (
      <td style={{ ...tdStyle, padding: '4px 12px' }}>
        <div dangerouslySetInnerHTML={{ __html: svgMap[smiles] }} style={{ maxWidth: 160, maxHeight: 80 }} />
      </td>
    );
  }
  return (
    <td style={{ ...tdStyle, padding: '4px 12px' }}>
      <StructureImage smiles={smiles} size={70} />
    </td>
  );
};

/* ---------- Main Component ---------- */
const WorkbenchMetabolism: React.FC = () => {
  // Seed from the ?smiles= popout param (same pattern as WorkbenchADMET), so a
  // structure passed in pre-loads instead of starting blank.
  const { smiles: ctxSmiles } = useWorkbenchContext();
  const [smiles, setSmiles] = useState(ctxSmiles || '');
  const [analysisType, setAnalysisType] = useState<AnalysisType>('liability');
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const [metaboliteSvgs, setMetaboliteSvgs] = useState<Record<string, string>>({});
  const [parentSvg, setParentSvg] = useState('');
  const [cypDockPose, setCypDockPose] = useState(1);

  // CYP docking extras
  const [cypPresets, setCypPresets] = useState<string[]>([]);
  const [selectedCyp, setSelectedCyp] = useState('cyp3a4');

  const { bailOutText, setHasSaved } = useToolStep();

  // Fetch CYP presets on mount
  useEffect(() => {
    apiClient.get('/v2/metabolism/cyp-presets').then((resp) => {
      const raw = resp.data?.presets || resp.data || [];
      if (Array.isArray(raw) && raw.length > 0) {
        setCypPresets(raw.map((p: any) => typeof p === 'string' ? p : p.key));
      }
      if (resp.data?.default) setSelectedCyp(resp.data.default);
    }).catch(() => {});
  }, []);

  const handleCancel = () => { abortRef.current?.abort(); };

  const runAnalysis = async () => {
    if (!smiles.trim()) return;
    setIsRunning(true);
    setError('');
    setResult(null);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const endpoints: Record<AnalysisType, string> = {
        liability: '/v2/metabolism/liability',
        predict: '/v2/metabolism/predict',
        secondary: '/v2/metabolism/secondary',
        full: '/v2/metabolism/full',
        'cyp-dock': '/v2/metabolism/cyp-dock',
      };
      const payload: any = { smiles };
      if (analysisType === 'cyp-dock') payload.cyp_preset = selectedCyp;

      const resp = await apiClient.post(endpoints[analysisType], payload, { signal: controller.signal });
      setResult(resp.data);

      // Fetch SVG depictions for metabolite SMILES
      const metabolites: string[] = [];
      if (analysisType === 'predict') (resp.data.metabolites || []).forEach((m: any) => { if (m.smiles) metabolites.push(m.smiles); });
      else if (analysisType === 'secondary') ((resp.data.secondary || resp.data).metabolites || []).forEach((m: any) => { if (m.smiles) metabolites.push(m.smiles); });
      else if (analysisType === 'full') ((resp.data.prediction || {}).metabolites || []).forEach((m: any) => { if (m.smiles) metabolites.push(m.smiles); });
      if (metabolites.length > 0) {
        try {
          const bulkResp = await apiClient.post('/v2/indigo/render_bulk',
            metabolites.slice(0, 50).map((smi: string) => ({ structure: { value: smi, type: 'RAW' }, width: '160px', height: '80px' })),
          );
          if (bulkResp.data && typeof bulkResp.data === 'object') {
            const svgMap: Record<string, string> = {};
            for (const [smi, item] of Object.entries(bulkResp.data as Record<string, { error?: string; result?: string }>)) {
              if (item.result) svgMap[smi] = atob(item.result);
            }
            setMetaboliteSvgs(svgMap);
          }
        } catch { /* depiction is optional */ }
      }

      // Fetch parent structure SVG for liability hotspot view
      if ((analysisType === 'liability' || analysisType === 'full') && payload.smiles) {
        try {
          const depResp = await apiClient.post('/v2/indigo/render_bulk',
            [{ structure: { value: payload.smiles, type: 'RAW' }, width: '320px', height: '240px' }],
          );
          if (depResp.data && typeof depResp.data === 'object') {
            for (const item of Object.values(depResp.data as Record<string, { error?: string; result?: string }>)) {
              if (item.result) { setParentSvg(atob(item.result)); break; }
            }
          }
        } catch { /* optional */ }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        setError(err.response?.data?.detail || err.message || 'Analysis failed');
      }
    } finally {
      setIsRunning(false);
      abortRef.current = null;
    }
  };

  const riskColor = (r: string) => r === 'Low' ? '#16a34a' : r === 'Medium' ? '#d97706' : '#dc2626';

  /* ---------- Result Renderers ---------- */

  const renderLiability = (d: any) => {
    const l = d.liability || d;
    const spots = l.soft_spots || [];
    const alerts = l.alerts || [];
    return (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 16 }}>
          <div style={resultCardStyle}>
            <div style={resultLabelStyle}>Risk Level</div>
            <div style={{ ...resultValueStyle, color: riskColor(l.risk_level || '') }}>{l.risk_level || '\u2014'}</div>
          </div>
          <div style={resultCardStyle}>
            <div style={resultLabelStyle}>Liability Score</div>
            <div style={resultValueStyle}>{l.overall_score != null ? l.overall_score.toFixed(1) : '\u2014'}</div>
          </div>
          <div style={resultCardStyle}>
            <div style={resultLabelStyle}>Soft Spots</div>
            <div style={{ ...resultValueStyle, color: spots.length > 5 ? '#d97706' : undefined }}>{spots.length}</div>
          </div>
        </div>
        {renderLiabilitySpots(spots)}
        {alerts.length > 0 && (
          <div style={{ marginTop: 12 }}>
            {alerts.map((a: string, i: number) => (
              <div key={i} style={{ color: '#dc2626', fontSize: 13, fontFamily: 'monospace', marginBottom: 4 }}>Alert: {a}</div>
            ))}
          </div>
        )}
      </>
    );
  };

  const renderLiabilitySpots = (spots: any[]) => (
    spots.length > 0 ? (
      <>
        {/* Molecule structure with hotspot legend */}
        {parentSvg && (
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ background: 'var(--card-bg)', borderRadius: 10, padding: 8, display: 'inline-block' }}>
              <div dangerouslySetInnerHTML={{ __html: parentSvg }} style={{ maxWidth: 320, maxHeight: 240 }} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <h3 style={{ ...sectionHeadingStyle, marginTop: 0 }}>Metabolic Hotspots</h3>
              {spots.map((s: any, i: number) => {
                const severity = s.reasons?.length > 2 ? '#dc2626' : s.reasons?.length > 1 ? '#d97706' : '#f59e0b';
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: `${severity}30`, border: `2px solid ${severity}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: severity, fontFamily: 'monospace', flexShrink: 0 }}>
                      {s.atom_index}
                    </div>
                    <div>
                      <span style={{ color: severity, fontWeight: 600, fontSize: 13 }}>{s.atom_symbol}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 11, marginLeft: 6 }}>{s.reasons?.join(', ') || ''}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <h3 style={sectionHeadingStyle}>Soft Spot Details</h3>
        <div style={tableContainerStyle}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ borderBottom: '1px solid var(--card-border)' }}>
              <th style={thStyle}>Atom Index</th><th style={thStyle}>Symbol</th><th style={thStyle}>Reasons</th>
            </tr></thead>
            <tbody>
              {spots.map((s: any, i: number) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--card-bg)' }}>
                  <td style={tdStyle}>{s.atom_index}</td>
                  <td style={{ ...tdStyle, color: '#f59e0b' }}>{s.atom_symbol}</td>
                  <td style={tdStyle}>{s.reasons?.join(', ') || '\u2014'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    ) : null
  );

  const renderMetabolites = (metabolites: any[], phaseLabel: string) => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 16 }}>
        <div style={resultCardStyle}>
          <div style={resultLabelStyle}>{phaseLabel} Metabolites</div>
          <div style={resultValueStyle}>{metabolites.length}</div>
        </div>
      </div>
      {metabolites.length > 0 && (
        <div style={tableContainerStyle}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ borderBottom: '1px solid var(--card-border)' }}>
              <th style={thStyle}>#</th><th style={thStyle}>Transformation</th><th style={thStyle}>Structure</th><th style={thStyle}>{'\u0394'} Mass (Da)</th>
            </tr></thead>
            <tbody>
              {metabolites.map((m: any, i: number) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--card-bg)' }}>
                  <td style={tdStyle}>{m.rank ?? i + 1}</td>
                  <td style={{ ...tdStyle, color: '#f59e0b' }}>{m.transformation}</td>
                  <SmilesSvgCell smiles={m.smiles || ''} svgMap={metaboliteSvgs} />
                  <td style={tdStyle}>{m.delta_mass != null ? `${m.delta_mass > 0 ? '+' : ''}${m.delta_mass.toFixed(3)}` : '\u2014'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  const renderPredict = (d: any) => {
    const metabolites = d.metabolites || [];
    return renderMetabolites(metabolites, 'Phase I');
  };

  const renderSecondary = (d: any) => {
    const sec = d.secondary || d;
    const metabolites = sec.metabolites || [];
    return renderMetabolites(metabolites, 'Phase II');
  };

  const renderFull = (d: any) => {
    const pred = d.prediction || {};
    const metabolites = pred.metabolites || [];
    const l = d.liability || {};
    const spots = l.soft_spots || [];
    return (
      <>
        {/* Liability summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 16 }}>
          <div style={resultCardStyle}>
            <div style={resultLabelStyle}>Risk Level</div>
            <div style={{ ...resultValueStyle, color: riskColor(l.risk_level || '') }}>{l.risk_level || '\u2014'}</div>
          </div>
          <div style={resultCardStyle}>
            <div style={resultLabelStyle}>Liability Score</div>
            <div style={resultValueStyle}>{l.overall_score != null ? l.overall_score.toFixed(1) : '\u2014'}</div>
          </div>
          <div style={resultCardStyle}>
            <div style={resultLabelStyle}>Soft Spots</div>
            <div style={{ ...resultValueStyle, color: spots.length > 5 ? '#d97706' : undefined }}>{spots.length}</div>
          </div>
          <div style={resultCardStyle}>
            <div style={resultLabelStyle}>Phase I Metabolites</div>
            <div style={resultValueStyle}>{metabolites.length}</div>
          </div>
        </div>
        {/* Metabolite table */}
        {metabolites.length > 0 && (
          <>
            <h3 style={sectionHeadingStyle}>Predicted Metabolites</h3>
            <div style={tableContainerStyle}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                  <th style={thStyle}>#</th><th style={thStyle}>Transformation</th><th style={thStyle}>Structure</th><th style={thStyle}>{'\u0394'} Mass (Da)</th>
                </tr></thead>
                <tbody>
                  {metabolites.map((m: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--card-bg)' }}>
                      <td style={tdStyle}>{m.rank ?? i + 1}</td>
                      <td style={{ ...tdStyle, color: '#f59e0b' }}>{m.transformation}</td>
                      <SmilesSvgCell smiles={m.smiles || ''} svgMap={metaboliteSvgs} />
                      <td style={tdStyle}>{m.delta_mass != null ? `${m.delta_mass > 0 ? '+' : ''}${m.delta_mass.toFixed(3)}` : '\u2014'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {/* Soft spot details */}
        {renderLiabilitySpots(spots)}
      </>
    );
  };

  const renderCypDock = (d: any) => {
    const modes = d.modes || [];
    const best = modes[0]?.affinity_kcal_mol;
    return (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 16 }}>
          <div style={resultCardStyle}>
            <div style={resultLabelStyle}>Target</div>
            <div style={{ ...resultValueStyle, fontSize: 16 }}>{d.receptor_label || d.receptor_source || selectedCyp.toUpperCase()}</div>
          </div>
          <div style={resultCardStyle}>
            <div style={resultLabelStyle}>Binding Poses</div>
            <div style={resultValueStyle}>{modes.length}</div>
          </div>
          {best != null && (
            <div style={resultCardStyle}>
              <div style={resultLabelStyle}>Best Affinity</div>
              <div style={{ ...resultValueStyle, color: best < -7 ? '#16a34a' : best < -5 ? '#d97706' : '#dc2626' }}>
                {best.toFixed(2)} kcal/mol
              </div>
            </div>
          )}
        </div>
        {/* Poses table */}
        {modes.length > 0 && (
          <div style={tableContainerStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                <th style={thStyle}>Pose</th><th style={thStyle}>Affinity (kcal/mol)</th>
              </tr></thead>
              <tbody>
                {modes.map((m: any) => (
                  <tr key={m.mode} onClick={() => setCypDockPose(m.mode)}
                    style={{ borderBottom: '1px solid var(--card-bg)', cursor: d.pose_excerpt ? 'pointer' : undefined, background: cypDockPose === m.mode ? 'rgba(245,158,11,0.08)' : undefined }}>
                    <td style={{ ...tdStyle, fontWeight: cypDockPose === m.mode ? 700 : undefined }}>{m.mode}</td>
                    <td style={{ ...tdStyle, color: m.affinity_kcal_mol < -7 ? '#16a34a' : m.affinity_kcal_mol < -5 ? '#d97706' : '#dc2626' }}>
                      {m.affinity_kcal_mol?.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* 3D Viewer */}
        {d.pose_excerpt && (
          <DockingPoseViewer
            poseData={d.pose_excerpt}
            bestAffinity={d.best_affinity_kcal_mol ?? best ?? 0}
            receptor={d.receptor_label || d.receptor_source || selectedCyp.toUpperCase()}
            currentPose={cypDockPose}
            onPoseChange={setCypDockPose}
          />
        )}
      </>
    );
  };

  const renderResult = () => {
    if (!result) return null;
    const renderers: Record<AnalysisType, (d: any) => React.ReactNode> = {
      liability: renderLiability,
      predict: renderPredict,
      secondary: renderSecondary,
      full: renderFull,
      'cyp-dock': renderCypDock,
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {renderers[analysisType](result)}
        {/* Interpretation */}
        {renderInterpretation()}
        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button onClick={() => navigator.clipboard.writeText(JSON.stringify(result, null, 2))} style={btnStyle}>Copy JSON</button>
          <button onClick={() => {
            const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `metabolism_${analysisType}.json`; a.click(); URL.revokeObjectURL(url);
          }} style={btnStyle}>Download JSON</button>
          <button onClick={() => {
            const lines: string[] = [];
            if (analysisType === 'liability' || analysisType === 'full') {
              const l = result.liability || result;
              lines.push('metric,value');
              if (l.risk_level) lines.push(`risk_level,${l.risk_level}`);
              if (l.overall_score != null) lines.push(`liability_score,${l.overall_score}`);
              const spots = l.soft_spots || [];
              if (spots.length > 0) { lines.push('', 'atom_index,symbol,reasons'); spots.forEach((s: any) => lines.push(`${s.atom_index},${s.atom_symbol},"${(s.reasons || []).join('; ')}"`)); }
            }
            if (analysisType === 'predict' || analysisType === 'full') {
              const mets = analysisType === 'full' ? (result.prediction?.metabolites || []) : (result.metabolites || []);
              if (mets.length > 0) { if (lines.length > 0) lines.push(''); lines.push('rank,transformation,smiles,delta_mass'); mets.forEach((m: any, i: number) => lines.push(`${m.rank ?? i + 1},${m.transformation},"${m.smiles || ''}",${m.delta_mass ?? ''}`)); }
            }
            if (analysisType === 'secondary') {
              const mets = (result.secondary || result).metabolites || [];
              if (mets.length > 0) { lines.push('rank,transformation,smiles,delta_mass'); mets.forEach((m: any, i: number) => lines.push(`${m.rank ?? i + 1},${m.transformation},"${m.smiles || ''}",${m.delta_mass ?? ''}`)); }
            }
            if (analysisType === 'cyp-dock') {
              const modes = result.modes || [];
              lines.push('pose,affinity_kcal_mol');
              modes.forEach((m: any) => lines.push(`${m.mode},${m.affinity_kcal_mol}`));
            }
            const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `metabolism_${analysisType}.csv`; a.click(); URL.revokeObjectURL(url);
          }} style={primaryBtnStyle}>Download CSV</button>
        </div>
      </div>
    );
  };

  const renderInterpretation = () => {
    if (!result) return null;
    let text = '';
    let color = '#d97706';

    if (analysisType === 'liability') {
      const l = result.liability || result;
      const risk = l.risk_level || 'Unknown';
      const spots = l.soft_spots || [];
      if (risk === 'Low') { text = `Low metabolic liability — ${spots.length} soft spot${spots.length !== 1 ? 's' : ''} identified.`; color = '#16a34a'; }
      else if (risk === 'Medium') { const topSpot = spots[0]; const detail = topSpot ? ` — atom ${topSpot.atom_index} (${topSpot.atom_symbol}) is the primary metabolic soft spot` : ''; text = `${spots.length} vulnerable sites identified${detail}.`; color = '#d97706'; }
      else { const topSpot = spots[0]; const detail = topSpot ? ` — atom ${topSpot.atom_index} (${topSpot.atom_symbol}) is the primary metabolic soft spot` : ''; text = `High metabolic liability with ${spots.length} soft spots${detail}.`; color = '#dc2626'; }
    } else if (analysisType === 'predict') {
      const n = (result.metabolites || []).length;
      if (n === 0) { text = 'No predicted metabolites — metabolically stable.'; color = '#16a34a'; }
      else if (n <= 3) { text = `${n} predicted Phase I metabolite${n > 1 ? 's' : ''} — moderate metabolism expected.`; color = '#d97706'; }
      else { text = `${n} predicted Phase I metabolites — extensive oxidative metabolism expected.`; color = '#dc2626'; }
    } else if (analysisType === 'full') {
      const risk = result.liability?.risk_level || 'Unknown';
      const n = (result.prediction?.metabolites || []).length;
      if (risk === 'Low') { text = `Low overall metabolic risk with ${n} predicted metabolite${n !== 1 ? 's' : ''}.`; color = '#16a34a'; }
      else if (risk === 'Medium') { text = `Moderate metabolic risk — ${n} metabolites predicted, consider structural optimization.`; color = '#d97706'; }
      else { text = `High metabolic risk — ${n} metabolites predicted, significant clearance expected.`; color = '#dc2626'; }
    } else if (analysisType === 'cyp-dock') {
      const best = result.modes?.[0]?.affinity_kcal_mol;
      if (best == null) { text = 'No docking results obtained.'; color = '#d97706'; }
      else if (best < -7) { text = `Strong CYP binding (${best.toFixed(1)} kcal/mol) — likely a CYP substrate, expect significant metabolism.`; color = '#dc2626'; }
      else if (best < -5) { text = `Moderate CYP binding (${best.toFixed(1)} kcal/mol) — may undergo CYP-mediated metabolism.`; color = '#d97706'; }
      else { text = `Weak CYP binding (${best.toFixed(1)} kcal/mol) — unlikely to be a major CYP substrate.`; color = '#16a34a'; }
    } else if (analysisType === 'secondary') {
      const sec = result.secondary || result;
      const n = (sec.metabolites || []).length;
      if (n === 0) { text = 'No Phase II conjugation metabolites predicted.'; color = '#16a34a'; }
      else { text = `${n} Phase II conjugation metabolite${n > 1 ? 's' : ''} predicted (glucuronidation, sulfation, methylation).`; color = '#d97706'; }
    }

    if (!text) return null;
    return (
      <div style={{ padding: '10px 14px', background: `${color}18`, border: `1px solid ${color}44`, borderRadius: 8, color, fontSize: 13, lineHeight: 1.5 }}>
        {text}
      </div>
    );
  };

  const methodCardForType: Record<AnalysisType, { title: string; desc: string; engine: 'Metabolism Hub' | 'Vina'; ref?: string; params?: { name: string; desc: string }[] }> = {
    liability: {
      title: 'Metabolic Liability Assessment',
      desc: 'Identifies positions on the molecule vulnerable to metabolic breakdown. Scores overall metabolic risk and highlights soft spots by atom.',
      engine: 'Metabolism Hub',
      params: [
        { name: 'Risk Level', desc: 'Low / Medium / High classification' },
        { name: 'Soft Spots', desc: 'Atom-level vulnerability with CYP enzyme reasons' },
        { name: 'Alerts', desc: 'Structural alerts for rapid metabolism' },
      ],
    },
    predict: {
      title: 'Primary Metabolite Prediction',
      desc: 'Predicts primary Phase I metabolites from CYP-mediated oxidation reactions. Returns ranked metabolite structures with mass deltas.',
      engine: 'Metabolism Hub',
      params: [
        { name: 'CYP Oxidation', desc: 'Hydroxylation, N-dealkylation, O-dealkylation' },
        { name: 'Ranking', desc: 'Metabolites ranked by likelihood' },
      ],
    },
    secondary: {
      title: 'Phase II Conjugation',
      desc: 'Predicts Phase II conjugation metabolites including glucuronidation, sulfation, and methylation pathways.',
      engine: 'Metabolism Hub',
      params: [
        { name: 'Glucuronidation', desc: 'UGT-mediated conjugation' },
        { name: 'Sulfation', desc: 'SULT-mediated conjugation' },
        { name: 'Methylation', desc: 'COMT/TPMT-mediated methylation' },
      ],
    },
    full: {
      title: 'Full Metabolism Analysis',
      desc: 'Complete metabolism report combining Phase I metabolite prediction with metabolic liability scoring in a single call.',
      engine: 'Metabolism Hub',
      params: [
        { name: 'Phase I', desc: 'CYP-mediated metabolite prediction' },
        { name: 'Liability', desc: 'Soft-spot identification and risk scoring' },
      ],
    },
    'cyp-dock': {
      title: 'CYP Active-Site Docking',
      desc: 'Docks the ligand into a CYP isoform active site using AutoDock Vina to predict metabolic binding affinity and pose.',
      engine: 'Vina',
      params: [
        { name: 'Affinity', desc: 'Binding energy in kcal/mol' },
        { name: 'Modes', desc: 'Multiple binding pose predictions' },
        { name: 'Pose', desc: '3D docking pose visualization' },
      ],
    },
  };

  const mc = methodCardForType[analysisType];

  return (
    <>
    <WorkbenchContextBar />
    <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Status bar */}
        <div style={statusBarStyle}>
          <Link to="/hubs" style={{ color: '#f59e0b', textDecoration: 'none' }}>Toolbox</Link>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <ToolSwitcher currentToolId="metabolism" />
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Engine: {analysisType === 'cyp-dock' ? 'AutoDock Vina' : 'Metabolism Hub'}</span>
          <div style={{ flex: 1 }} />
          <CancelButton onCancel={handleCancel} isRunning={isRunning} />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={h2Style}>Metabolism Analysis</h2>
            <p style={descStyle}>Predict metabolic fate, identify soft spots, and dock into CYP active sites.</p>

            {/* Analysis type tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
              {ANALYSIS_TABS.map((tab) => (
                <button key={tab.key} onClick={() => { setAnalysisType(tab.key); setResult(null); setError(''); }}
                  style={{
                    ...btnStyle,
                    background: analysisType === tab.key ? 'rgba(245, 158, 11, 0.15)' : 'var(--card-bg)',
                    borderColor: analysisType === tab.key ? '#f59e0b' : 'var(--card-border)',
                    color: analysisType === tab.key ? '#f59e0b' : 'var(--text-primary)',
                    fontSize: 12,
                    padding: '6px 12px',
                  }}>
                  {tab.label}
                </button>
              ))}
            </div>
            <p style={{ ...descStyle, fontSize: 12, color: 'var(--text-secondary)' }}>{ANALYSIS_TABS.find(t => t.key === analysisType)?.desc}</p>

            {/* SMILES input */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button onClick={() => setSmiles('CC(=O)Oc1ccccc1C(=O)O')} style={btnStyle}>Aspirin</button>
              <button onClick={() => setSmiles('c1ccc2[nH]c(-c3ccccc3)nc2c1')} style={btnStyle}>Omeprazole core</button>
              <button onClick={() => setSmiles('CN1C(=O)CN=C(c2ccccc2)c2cc(Cl)ccc21')} style={btnStyle}>Diazepam</button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input type="text" value={smiles} onChange={(e) => setSmiles(e.target.value)}
                placeholder="Enter molecule notation..."
                onKeyDown={(e) => { if (e.key === 'Enter') runAnalysis(); }}
                style={{ ...textareaStyle, height: 40, flex: 1 }} />
              {analysisType === 'cyp-dock' && cypPresets.length > 0 && (
                <select value={selectedCyp} onChange={(e) => setSelectedCyp(e.target.value)}
                  style={{ ...textareaStyle, height: 40, width: 140, flex: 'none' }}>
                  {cypPresets.map((p) => <option key={p} value={p}>{p.toUpperCase()}</option>)}
                </select>
              )}
              <button onClick={runAnalysis} disabled={!smiles.trim() || isRunning}
                style={{ ...primaryBtnStyle, opacity: smiles.trim() && !isRunning ? 1 : 0.5 }}>
                {isRunning ? 'Running...' : 'Run'}
              </button>
            </div>

            {/* Loading */}
            {isRunning && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={spinnerStyle} />
                <span style={{ color: '#f59e0b' }}>
                  {analysisType === 'cyp-dock' ? 'Docking into CYP active site...' :
                   analysisType === 'full' ? 'Running full metabolism analysis...' :
                   'Computing metabolism predictions...'}
                </span>
              </div>
            )}
            {error && <div style={errorStyle}>{error}</div>}

            {/* Results */}
            {renderResult()}
          </div>

          {/* Method card sidebar */}
          <div style={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <MethodCard
              engine={mc.engine}
              title={mc.title}
              description={mc.desc}
              parameters={mc.params}
              source={mc.engine === 'Vina' ? 'AutoDock Vina via CYP PDB structures' : 'CYP oxidation rule engine + soft-spot scoring'}
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

/* ---------- Styles ---------- */
const statusBarStyle: React.CSSProperties = { padding: '8px 20px', background: 'var(--hover-bg)', borderBottom: '1px solid var(--card-border)', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 };
const h2Style: React.CSSProperties = { color: '#f5f5f5', fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 0 };
const descStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: 13, marginBottom: 12, lineHeight: 1.5 };
const btnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const primaryBtnStyle: React.CSSProperties = { ...btnStyle, background: '#f59e0b', color: 'var(--content-bg)', border: '1px solid #f59e0b', fontWeight: 600 };
const textareaStyle: React.CSSProperties = { width: '100%', padding: '10px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 14, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' as const };
const errorStyle: React.CSSProperties = { marginTop: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fb7185', fontSize: 13 };
const spinnerStyle: React.CSSProperties = { width: 20, height: 20, border: '3px solid var(--card-border)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' };
const resultCardStyle: React.CSSProperties = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '12px 14px' };
const resultLabelStyle: React.CSSProperties = { color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em', marginBottom: 4 };
const resultValueStyle: React.CSSProperties = { color: '#f59e0b', fontSize: 18, fontWeight: 700, fontFamily: 'monospace' };
const sectionHeadingStyle: React.CSSProperties = { color: '#a7abb6', fontSize: 14, fontWeight: 600, margin: '8px 0 10px' };
const tableContainerStyle: React.CSSProperties = { background: 'var(--card-bg)', borderRadius: 10, border: '1px solid var(--card-border)', overflow: 'hidden', marginBottom: 16 };
const thStyle: React.CSSProperties = { padding: '8px 12px', textAlign: 'left' as const, color: '#a7abb6', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' };
const tdStyle: React.CSSProperties = { padding: '6px 12px', color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: 13 };

export default WorkbenchMetabolism;
